/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useToast } from '../ui/use-toast';
import { Loader2, Plus, Edit, Trash2, Users, Tag } from 'lucide-react';
import { eventsActions } from '../../actions/events.action';
import { EventRecord, EventTag, EventFormData, EventRegistrant } from '../../types/events/events';

const emptyForm: EventFormData = {
  title: '',
  description: '',
  host: '',
  city: '',
  format: '',
  date: '',
  seats: 0,
  imageUrl: '',
};

// ISO string -> value for <input type="datetime-local">
function toLocalInput(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
}

const EventsManagement = () => {
  const [events, setEvents] = useState<EventRecord[]>([]);
  const [tags, setTags] = useState<EventTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<EventRecord | null>(null);
  const [form, setForm] = useState<EventFormData>(emptyForm);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // registrants dialog
  const [registrantsEvent, setRegistrantsEvent] = useState<EventRecord | null>(null);
  const [registrants, setRegistrants] = useState<EventRegistrant[]>([]);
  const [registrantsLoading, setRegistrantsLoading] = useState(false);

  // tag manager
  const [showTags, setShowTags] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [tagLoading, setTagLoading] = useState(false);

  const { toast } = useToast();

  useEffect(() => {
    fetchEvents();
    fetchTags();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const res = await eventsActions.getEvents();
      setEvents(res.data || []);
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to fetch events', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const res = await eventsActions.getTags();
      setTags(res.data || []);
    } catch (e) {
      /* non-fatal */
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (ev: EventRecord) => {
    setEditing(ev);
    setForm({
      title: ev.title,
      description: ev.description,
      host: ev.host,
      city: ev.city,
      format: ev.format,
      date: toLocalInput(ev.date),
      seats: ev.seats,
      imageUrl: ev.imageUrl || '',
    });
    setShowForm(true);
  };

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setFormLoading(true);
      const payload = { ...form, date: new Date(form.date).toISOString(), seats: Number(form.seats) };
      if (editing) {
        await eventsActions.updateEvent(editing._id, payload);
        toast({ title: 'Success', description: 'Event updated' });
      } else {
        await eventsActions.addEvent(payload);
        toast({ title: 'Success', description: 'Event created' });
      }
      setShowForm(false);
      fetchEvents();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to save event', variant: 'destructive' });
    } finally {
      setFormLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      setDeleteLoading(true);
      await eventsActions.deleteEvent(deleteId);
      toast({ title: 'Success', description: 'Event deleted' });
      setDeleteId(null);
      fetchEvents();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to delete event', variant: 'destructive' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const openRegistrants = async (ev: EventRecord) => {
    setRegistrantsEvent(ev);
    try {
      setRegistrantsLoading(true);
      const res = await eventsActions.getRegistrants(ev._id);
      setRegistrants(res.data || []);
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to fetch registrants', variant: 'destructive' });
    } finally {
      setRegistrantsLoading(false);
    }
  };

  const addTag = async () => {
    if (!newTag.trim()) return;
    try {
      setTagLoading(true);
      await eventsActions.addTag({ label: newTag.trim(), value: newTag.trim() });
      setNewTag('');
      fetchTags();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to add tag', variant: 'destructive' });
    } finally {
      setTagLoading(false);
    }
  };

  const removeTag = async (id: string) => {
    try {
      await eventsActions.deleteTag(id);
      fetchTags();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to delete tag', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Events Management</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowTags(true)}>
            <Tag className="h-4 w-4 mr-2" />
            Manage Tags
          </Button>
          <Button onClick={openAdd}>
            <Plus className="h-4 w-4 mr-2" />
            Add Event
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Events List</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>City</TableHead>
                <TableHead>Format</TableHead>
                <TableHead>Host</TableHead>
                <TableHead>Booked</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((ev) => (
                <TableRow key={ev._id}>
                  <TableCell className="font-medium">{ev.title}</TableCell>
                  <TableCell>{ev.date ? new Date(ev.date).toLocaleString() : '-'}</TableCell>
                  <TableCell>{ev.city}</TableCell>
                  <TableCell>{ev.format}</TableCell>
                  <TableCell>{ev.host}</TableCell>
                  <TableCell>
                    {ev.taken}/{ev.seats}
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" onClick={() => openEdit(ev)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openRegistrants(ev)}>
                        <Users className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setDeleteId(ev._id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {events.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-6">
                    No events yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Event' : 'Add New Event'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitForm} className="space-y-3">
            <div>
              <Label>Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Host</Label>
                <Input
                  value={form.host}
                  onChange={(e) => setForm({ ...form, host: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Format</Label>
                <Select
                  value={form.format}
                  onValueChange={(v) => setForm({ ...form, format: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select format" />
                  </SelectTrigger>
                  <SelectContent>
                    {tags.map((t) => (
                      <SelectItem key={t._id} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Seats</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.seats}
                  onChange={(e) => setForm({ ...form, seats: Number(e.target.value) })}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date &amp; time</Label>
                <Input
                  type="datetime-local"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Image URL (optional)</Label>
                <Input
                  value={form.imageUrl}
                  onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={formLoading || !form.format}>
                {formLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <div className="py-4">Are you sure you want to delete this event?</div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)} disabled={deleteLoading}>
              No
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleteLoading}>
              {deleteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Yes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Registrants */}
      <Dialog open={!!registrantsEvent} onOpenChange={() => setRegistrantsEvent(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Registrants · {registrantsEvent?.title}</DialogTitle>
          </DialogHeader>
          {registrantsLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Reserved</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {registrants.map((r) => (
                  <TableRow key={r._id}>
                    <TableCell>
                      {[r.user?.firstName, r.user?.lastName].filter(Boolean).join(' ') || '—'}
                    </TableCell>
                    <TableCell>{r.user?.email || '—'}</TableCell>
                    <TableCell>{new Date(r.createdAt).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
                {registrants.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground py-6">
                      No registrants yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {/* Tag manager */}
      <Dialog open={showTags} onOpenChange={setShowTags}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Event Tags</DialogTitle>
          </DialogHeader>
          <div className="flex gap-2">
            <Input
              placeholder="New tag (e.g. Summit)"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
            />
            <Button onClick={addTag} disabled={tagLoading || !newTag.trim()}>
              {tagLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
            </Button>
          </div>
          <div className="mt-3 space-y-2">
            {tags.map((t) => (
              <div
                key={t._id}
                className="flex items-center justify-between rounded border px-3 py-2"
              >
                <span>{t.label}</span>
                <Button variant="outline" size="sm" onClick={() => removeTag(t._id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {tags.length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-4">No tags yet.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventsManagement;
